import { Refine, Authenticated } from '@refinedev/core';
import routerProvider from '@refinedev/react-router';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { dataProvider } from './providers/dataProvider';
import { authProvider } from './providers/authProvider';
import { ReactNode, Component, ErrorInfo } from 'react';

// Error Boundary to catch and display errors
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 bg-red-50 min-h-screen">
          <h1 className="text-2xl font-bold text-red-800 mb-4">Something went wrong</h1>
          <pre className="bg-white p-4 rounded border border-red-200 overflow-auto text-sm">
            {this.state.error?.message}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pages
import { LoginPage } from './pages/login';
import { DashboardPage } from './pages/dashboard';
import { RoomsList, RoomsCreate, RoomsEdit, RoomsShow } from './pages/rooms';
import { ActivitiesList, ActivitiesCreate, ActivitiesEdit } from './pages/activities';
import { DiningList, DiningCreate, DiningEdit } from './pages/dining';
import { EventsList, EventsCreate, EventsEdit } from './pages/events';
import { ToursList, ToursCreate, ToursEdit } from './pages/tours';
import { AttractionsList, AttractionsCreate, AttractionsEdit } from './pages/attractions';
import { HeroSlidesList, HeroSlidesCreate, HeroSlidesEdit } from './pages/hero-slides';
import { HomeSectionsList, HomeSectionsEdit } from './pages/home-sections';
import { SiteSettingsPage } from './pages/site-settings';
import { ConferenceRoomsList, ConferenceRoomsCreate, ConferenceRoomsEdit } from './pages/conference-rooms';
import { MeetingPackagesList, MeetingPackagesCreate, MeetingPackagesEdit } from './pages/meeting-packages';
import { OffersList, OffersCreate, OffersEdit } from './pages/offers';
import { PagesList, PagesCreate, PagesEdit } from './pages/pages';
import { OutboundPackagesList, OutboundPackagesCreate, OutboundPackagesEdit } from './pages/outbound-packages';
import { Layout } from './components/Layout';

// Wrapper for authenticated pages with layout
function AuthenticatedPage({ children }: { children: ReactNode }) {
  return (
    <Authenticated
      key="authenticated-routes"
      fallback={<Navigate to="/login" />}
      loading={<div className="min-h-screen flex items-center justify-center">Loading...</div>}
    >
      <Layout>
        <ErrorBoundary>{children}</ErrorBoundary>
      </Layout>
    </Authenticated>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        resources={[
          {
            name: 'rooms',
            list: '/rooms',
            create: '/rooms/create',
            edit: '/rooms/edit/:id',
            show: '/rooms/show/:id',
            meta: { label: 'Rooms', icon: 'bed' },
          },
          {
            name: 'activities',
            list: '/activities',
            create: '/activities/create',
            edit: '/activities/edit/:id',
            meta: { label: 'Activities', icon: 'activity' },
          },
          {
            name: 'dining',
            list: '/dining',
            create: '/dining/create',
            edit: '/dining/edit/:id',
            meta: { label: 'Dining', icon: 'utensils' },
          },
          {
            name: 'events',
            list: '/events',
            create: '/events/create',
            edit: '/events/edit/:id',
            meta: { label: 'Events', icon: 'calendar' },
          },
          {
            name: 'tours',
            list: '/tours',
            create: '/tours/create',
            edit: '/tours/edit/:id',
            meta: { label: 'Tours', icon: 'map' },
          },
          {
            name: 'attractions',
            list: '/attractions',
            create: '/attractions/create',
            edit: '/attractions/edit/:id',
            meta: { label: 'Attractions', icon: 'map-pin' },
          },
          {
            name: 'outbound-packages',
            list: '/outbound-packages',
            create: '/outbound-packages/create',
            edit: '/outbound-packages/edit/:id',
            meta: { label: 'Outbound Packages', icon: 'compass' },
          },
          {
            name: 'hero-slides',
            list: '/hero-slides',
            create: '/hero-slides/create',
            edit: '/hero-slides/edit/:id',
            meta: { label: 'Hero Slides', icon: 'image' },
          },
          {
            name: 'home-sections',
            list: '/home-sections',
            edit: '/home-sections/edit/:id',
            meta: { label: 'Home Sections', icon: 'layout' },
          },
          {
            name: 'site-settings',
            list: '/site-settings',
            meta: { label: 'Site Settings', icon: 'settings' },
          },
          {
            name: 'conference-rooms',
            list: '/conference-rooms',
            create: '/conference-rooms/create',
            edit: '/conference-rooms/edit/:id',
            meta: { label: 'Conference Rooms', icon: 'building' },
          },
          {
            name: 'meeting-packages',
            list: '/meeting-packages',
            create: '/meeting-packages/create',
            edit: '/meeting-packages/edit/:id',
            meta: { label: 'Meeting Packages', icon: 'package' },
          },
          {
            name: 'offers',
            list: '/offers',
            create: '/offers/create',
            edit: '/offers/edit/:id',
            meta: { label: 'Offers & Packages', icon: 'tag' },
          },
          {
            name: 'pages',
            list: '/pages',
            create: '/pages/create',
            edit: '/pages/edit/:id',
            meta: { label: 'Pages', icon: 'file-text' },
          },
        ]}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
        }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Dashboard */}
          <Route path="/" element={<AuthenticatedPage><DashboardPage /></AuthenticatedPage>} />

          {/* Rooms */}
          <Route path="/rooms" element={<AuthenticatedPage><RoomsList /></AuthenticatedPage>} />
          <Route path="/rooms/create" element={<AuthenticatedPage><RoomsCreate /></AuthenticatedPage>} />
          <Route path="/rooms/edit/:id" element={<AuthenticatedPage><RoomsEdit /></AuthenticatedPage>} />
          <Route path="/rooms/show/:id" element={<AuthenticatedPage><RoomsShow /></AuthenticatedPage>} />

          {/* Activities */}
          <Route path="/activities" element={<AuthenticatedPage><ActivitiesList /></AuthenticatedPage>} />
          <Route path="/activities/create" element={<AuthenticatedPage><ActivitiesCreate /></AuthenticatedPage>} />
          <Route path="/activities/edit/:id" element={<AuthenticatedPage><ActivitiesEdit /></AuthenticatedPage>} />

          {/* Dining */}
          <Route path="/dining" element={<AuthenticatedPage><DiningList /></AuthenticatedPage>} />
          <Route path="/dining/create" element={<AuthenticatedPage><DiningCreate /></AuthenticatedPage>} />
          <Route path="/dining/edit/:id" element={<AuthenticatedPage><DiningEdit /></AuthenticatedPage>} />

          {/* Events */}
          <Route path="/events" element={<AuthenticatedPage><EventsList /></AuthenticatedPage>} />
          <Route path="/events/create" element={<AuthenticatedPage><EventsCreate /></AuthenticatedPage>} />
          <Route path="/events/edit/:id" element={<AuthenticatedPage><EventsEdit /></AuthenticatedPage>} />

          {/* Tours */}
          <Route path="/tours" element={<AuthenticatedPage><ToursList /></AuthenticatedPage>} />
          <Route path="/tours/create" element={<AuthenticatedPage><ToursCreate /></AuthenticatedPage>} />
          <Route path="/tours/edit/:id" element={<AuthenticatedPage><ToursEdit /></AuthenticatedPage>} />

          {/* Attractions */}
          <Route path="/attractions" element={<AuthenticatedPage><AttractionsList /></AuthenticatedPage>} />
          <Route path="/attractions/create" element={<AuthenticatedPage><AttractionsCreate /></AuthenticatedPage>} />
          <Route path="/attractions/edit/:id" element={<AuthenticatedPage><AttractionsEdit /></AuthenticatedPage>} />

          {/* Outbound Packages */}
          <Route path="/outbound-packages" element={<AuthenticatedPage><OutboundPackagesList /></AuthenticatedPage>} />
          <Route path="/outbound-packages/create" element={<AuthenticatedPage><OutboundPackagesCreate /></AuthenticatedPage>} />
          <Route path="/outbound-packages/edit/:id" element={<AuthenticatedPage><OutboundPackagesEdit /></AuthenticatedPage>} />

          {/* Hero Slides */}
          <Route path="/hero-slides" element={<AuthenticatedPage><HeroSlidesList /></AuthenticatedPage>} />
          <Route path="/hero-slides/create" element={<AuthenticatedPage><HeroSlidesCreate /></AuthenticatedPage>} />
          <Route path="/hero-slides/edit/:id" element={<AuthenticatedPage><HeroSlidesEdit /></AuthenticatedPage>} />

          {/* Home Sections */}
          <Route path="/home-sections" element={<AuthenticatedPage><HomeSectionsList /></AuthenticatedPage>} />
          <Route path="/home-sections/edit/:id" element={<AuthenticatedPage><HomeSectionsEdit /></AuthenticatedPage>} />

          {/* Site Settings */}
          <Route path="/site-settings" element={<AuthenticatedPage><SiteSettingsPage /></AuthenticatedPage>} />

          {/* Conference Rooms */}
          <Route path="/conference-rooms" element={<AuthenticatedPage><ConferenceRoomsList /></AuthenticatedPage>} />
          <Route path="/conference-rooms/create" element={<AuthenticatedPage><ConferenceRoomsCreate /></AuthenticatedPage>} />
          <Route path="/conference-rooms/edit/:id" element={<AuthenticatedPage><ConferenceRoomsEdit /></AuthenticatedPage>} />

          {/* Meeting Packages */}
          <Route path="/meeting-packages" element={<AuthenticatedPage><MeetingPackagesList /></AuthenticatedPage>} />
          <Route path="/meeting-packages/create" element={<AuthenticatedPage><MeetingPackagesCreate /></AuthenticatedPage>} />
          <Route path="/meeting-packages/edit/:id" element={<AuthenticatedPage><MeetingPackagesEdit /></AuthenticatedPage>} />

          {/* Offers */}
          <Route path="/offers" element={<AuthenticatedPage><OffersList /></AuthenticatedPage>} />
          <Route path="/offers/create" element={<AuthenticatedPage><OffersCreate /></AuthenticatedPage>} />
          <Route path="/offers/edit/:id" element={<AuthenticatedPage><OffersEdit /></AuthenticatedPage>} />

          {/* Pages */}
          <Route path="/pages" element={<AuthenticatedPage><PagesList /></AuthenticatedPage>} />
          <Route path="/pages/create" element={<AuthenticatedPage><PagesCreate /></AuthenticatedPage>} />
          <Route path="/pages/edit/:id" element={<AuthenticatedPage><PagesEdit /></AuthenticatedPage>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Refine>
    </BrowserRouter>
  );
}

export default App;
